import { useQuery, useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  getDealershipGroups,
  getDealershipsInGroup,
  getGroupDashboardStats,
  getDealershipDashboardStats,
} from '../services/dealership.api'
import { CACHE_CONFIG } from '../common/constants'
import { getIntervalIsoRange, type GroupInterval } from '../utils/groupDashboardInterval'

/**
 * React Query hooks for dashboard data
 * Provides automatic caching, deduplication, and background refetching
 */

/**
 * Fetch all dealership groups
 * Cached for 10 minutes, kept in memory for 30 minutes
 */
export function useDealershipGroups(enabled = true) {
  return useQuery({
    queryKey: ['dealership-groups'],
    queryFn: getDealershipGroups,
    enabled,
    staleTime: CACHE_CONFIG.STALE_TIME.GROUPS,
    gcTime: CACHE_CONFIG.GC_TIME.DEFAULT,
    retry: CACHE_CONFIG.RETRY_COUNT,
    refetchOnWindowFocus: false,
  })
}

/**
 * Fetch dealerships for a specific group
 * Cached for 10 minutes, kept in memory for 30 minutes
 */
export function useDealershipsInGroup(groupId: string | null) {
  return useQuery({
    queryKey: ['dealerships', groupId],
    queryFn: () => getDealershipsInGroup(groupId!),
    enabled: !!groupId,
    staleTime: CACHE_CONFIG.STALE_TIME.DEALERSHIPS,
    gcTime: CACHE_CONFIG.GC_TIME.DEFAULT,
    retry: CACHE_CONFIG.RETRY_COUNT,
    refetchOnWindowFocus: false,
  })
}

/**
 * Fetch group dashboard stats (must use same queryKey as DealershipProvider for cache sharing)
 */
export function useGroupDashboardStats(groupId: string | null, interval: GroupInterval = 'this_week') {
  const { startDate, endDate } = useMemo(() => getIntervalIsoRange(interval), [interval])
  return useQuery({
    queryKey: ['group-stats', groupId, interval],
    queryFn: () => getGroupDashboardStats(groupId!, startDate, endDate),
    enabled: !!groupId,
    staleTime: CACHE_CONFIG.STALE_TIME.STATS,
    gcTime: CACHE_CONFIG.GC_TIME.DEFAULT,
    retry: CACHE_CONFIG.RETRY_COUNT,
    refetchOnWindowFocus: false,
  })
}

/**
 * Fetch dealership dashboard stats
 * Cached for 5 minutes, kept in memory for 30 minutes
 */
export function useDealershipDashboardStats(dealershipId: string | null, interval: GroupInterval = 'this_week') {
  const { startDate, endDate } = useMemo(() => getIntervalIsoRange(interval), [interval])
  return useQuery({
    queryKey: ['dealership-stats', dealershipId, interval],
    queryFn: () => getDealershipDashboardStats(dealershipId!, startDate, endDate),
    enabled: !!dealershipId,
    staleTime: CACHE_CONFIG.STALE_TIME.STATS,
    gcTime: CACHE_CONFIG.GC_TIME.DEFAULT,
    retry: CACHE_CONFIG.RETRY_COUNT,
    refetchOnWindowFocus: false,
  })
}

/**
 * Fetch stats for multiple dealerships in parallel
 * Uses React Query's useQueries for optimal batching
 * Cached for 5 minutes, kept in memory for 30 minutes
 */
export function useMultipleDealershipStats(dealershipIds: string[], interval: GroupInterval = 'this_week') {
  const { startDate, endDate } = useMemo(() => getIntervalIsoRange(interval), [interval])
  return useQueries({
    queries: dealershipIds.map(id => ({
      queryKey: ['dealership-stats', id, interval],
      queryFn: () => getDealershipDashboardStats(id, startDate, endDate),
      staleTime: CACHE_CONFIG.STALE_TIME.STATS,
      gcTime: CACHE_CONFIG.GC_TIME.DEFAULT,
      retry: CACHE_CONFIG.RETRY_COUNT,
      refetchOnWindowFocus: false,
      enabled: !!id,
    })),
  })
}

/**
 * Fetch stats for multiple groups in parallel
 */
export function useMultipleGroupStats(groupIds: string[], interval: GroupInterval = 'this_week') {
  const { startDate, endDate } = useMemo(() => getIntervalIsoRange(interval), [interval])
  return useQueries({
    queries: groupIds.map(id => ({
      queryKey: ['group-stats', id, interval],
      queryFn: () => getGroupDashboardStats(id, startDate, endDate),
      staleTime: CACHE_CONFIG.STALE_TIME.STATS,
      gcTime: CACHE_CONFIG.GC_TIME.DEFAULT,
      retry: CACHE_CONFIG.RETRY_COUNT,
      refetchOnWindowFocus: false,
      enabled: !!id,
    })),
  })
}

